import { useLanguage } from "./LanguageContext.jsx";
// Reconstructed from production code; original paths and local names were not available.
import React from "react";
import { api } from "../services/api.js";
import * as jsxRuntime from "react/jsx-runtime";
const CartContext = React.createContext(null);
function CartProvider({ children: children }) {
  const { tText } = useLanguage();
  const [cartId, setCartId] = React.useState(null),
    [items, setItems] = React.useState([]),
    [total, setTotal] = React.useState(0),
    [loading, setLoading] = React.useState(false),
    [error, setError] = React.useState(""),
    cartIdRef = React.useRef(null),
    rememberCartId = (K) => {
      const J = K ? String(K) : null;
      cartIdRef.current = J;
      setCartId(J);
      J ? localStorage.setItem("cartId", J) : localStorage.removeItem("cartId");
      return J;
    },
    clearCartState = () => {
      rememberCartId(null);
      setItems([]);
      setTotal(0);
      setError("");
    };
  React.useEffect(() => {
    const K = localStorage.getItem("cartId");
    K
      ? (rememberCartId(K), loadCart(K).catch(() => {}))
      : createCart().catch(() => {});
  }, []);
  const createCart = async () => {
      try {
        setLoading(true);
        const K = await api.post("/cart"),
          J = rememberCartId(K.data.id);
        setItems([]);
        setTotal(0);
        setError("");
        return J;
      } catch (K) {
        throw (console.error(K), setError(tText("Unable to create cart")), K);
      } finally {
        setLoading(false);
      }
    },
    startFreshCart = async () => (clearCartState(), createCart()),
    loadCart = async (K = cartId) => {
      if (!K) return createCart();
      const J = String(K);
      try {
        setLoading(true);
        const Q = await api.get(`/cart/${J}`);
        cartIdRef.current === J &&
          (setItems(Q.data.items || []),
          setTotal(Q.data.total || 0),
          setError(""));
        return Q.data;
      } catch (Q) {
        if ((console.error(Q), Q.response?.status === 404))
          return cartIdRef.current !== J
            ? null
            : (clearCartState(), createCart());
        throw (
          cartIdRef.current === J &&
            setError(Q.response?.data?.message || tText("Unable to load cart")),
          Q
        );
      } finally {
        setLoading(false);
      }
    },
    cartContextValue = {
      cartId: cartId,
      items: items,
      total: total,
      loading: loading,
      error: error,
      addToCart: async (K, J = null, Q = 1) => {
        let W = cartIdRef.current || cartId;
        W || (W = await createCart());
        const te = (oe) =>
          api.post(`/cart/${oe}/items`, {
            product_id: K,
            variant_id: J,
            quantity: Q,
          });
        try {
          setLoading(true);
          let oe;
          try {
            oe = await te(W);
          } catch (he) {
            if (he.response?.status !== 404) throw he;
            cartIdRef.current === String(W) && clearCartState();
            W = await createCart();
            oe = await te(W);
          }
          setItems(oe.data.items || []);
          setTotal(oe.data.total || 0);
          setError("");
          return oe.data;
        } catch (oe) {
          throw (
            console.error(oe),
            setError(oe.response?.data?.message || tText("Unable to add to cart")),
            oe
          );
        } finally {
          setLoading(false);
        }
      },
      updateItemQuantity: async (K, J) => {
        const Q = cartIdRef.current || cartId;
        if (Q)
          try {
            setLoading(true);
            const W = await api.put(`/cart/${Q}/items/${K}`, {
              quantity: J,
            });
            setItems(W.data.items || []);
            setTotal(W.data.total || 0);
            setError("");
          } catch (W) {
            if ((console.error(W), W.response?.status === 404)) {
              if (cartIdRef.current !== String(Q)) return;
              clearCartState();
              await createCart();
              return;
            }
            throw (
              setError(W.response?.data?.message || tText("Unable to update item")),
              W
            );
          } finally {
            setLoading(false);
          }
      },
      removeItem: async (K) => {
        const J = cartIdRef.current || cartId;
        if (J)
          try {
            setLoading(true);
            const Q = await api.delete(`/cart/${J}/items/${K}`);
            setItems(Q.data.items || []);
            setTotal(Q.data.total || 0);
            setError("");
          } catch (Q) {
            if ((console.error(Q), Q.response?.status === 404)) {
              if (cartIdRef.current !== String(J)) return;
              clearCartState();
              await createCart();
              return;
            }
            throw (
              setError(Q.response?.data?.message || tText("Unable to remove item")),
              Q
            );
          } finally {
            setLoading(false);
          }
      },
      fetchCart: loadCart,
      startFreshCart: startFreshCart,
    };
  return (
    <CartContext.Provider value={cartContextValue}>
      {children}
    </CartContext.Provider>
  );
}
function useCart() {
  const l = React.useContext(CartContext);
  if (!l) throw new Error("useCart must be used inside <CartProvider>");
  return l;
}
export { CartContext, CartProvider, useCart };
